/**
 * Sync SSE Event Reducers
 *
 * Pure functions that merge parsed `sync_started` / `sync_progress` SSE
 * events onto a SyncStatus snapshot. Extracted from monitoringStore.ts so
 * BOTH SSE paths (the store's connectSse() listeners on the admin
 * SyncMonitoring page AND the shared `syncClient` pipe used by
 * ImportProgressIndicator on /import) share one set of merge rules — and so
 * those rules are unit-testable without a live EventSource.
 */

import type { ActiveSync, SseSyncEvent, SyncStatus } from '../types'

/**
 * `sync_started` / `sync_progress` events arrive from TWO backend emitters
 * with different payload shapes: the sync-stream 2s poll broadcaster (full
 * cards) and the job-manager worker-push relay, which maps lifecycle
 * 'started'/'retried'/'progress' onto these event names with payloads that
 * can lack startedAt / sourceName / type / recordsProcessed / currentStage.
 * Typing the reducer input as deep-partial forces every field below to
 * default through the existing card instead of clobbering it to undefined.
 */
export type PartialSseSyncEvent = Partial<SseSyncEvent> & Pick<SseSyncEvent, 'jobId'>

/**
 * Merge a `sync_started` event into the snapshot. Deduplicates by jobId:
 * the worker push and the 2s backend poll both emit sync_started for the
 * same job, so an existing card is updated in-place instead of appending a
 * duplicate. Every field a partial relay event can omit defaults through
 * the existing card — a partial event must NEVER blank seeded fields
 * (a clobbered `startedAt: undefined` used to crash formatDistanceToNow
 * with a RangeError inside the globally mounted ImportProgressIndicator).
 */
export function reduceSyncStartedEvent(
  currentStatus: SyncStatus,
  event: PartialSseSyncEvent,
): SyncStatus {
  const existingCard = currentStatus.activeSyncs.find((sync) => sync.jobId === event.jobId)
  // Fallback for a genuinely NEW card born from a partial relay event:
  // "now" is the honest approximation of startedAt. It MUST be a parseable
  // ISO string — downstream renders do `new Date(startedAt)`.
  const startedAtFallback = existingCard?.startedAt ?? new Date().toISOString()
  const mergedCard: ActiveSync = {
    jobId: event.jobId,
    sourceId: event.sourceId ?? existingCard?.sourceId ?? null,
    sourceName: event.sourceName ?? existingCard?.sourceName ?? '',
    type: event.type ?? existingCard?.type ?? 'api',
    status: event.status ?? existingCard?.status ?? 'processing',
    queueName: existingCard?.queueName ?? 'import-jobs',
    priority: existingCard?.priority ?? 5,
    // Numeric fields default through the card and then 0 so a partial event
    // can never produce undefined numbers (which would crash
    // `.toLocaleString()` renders downstream).
    progress: event.progress ?? existingCard?.progress ?? 0,
    recordsProcessed: event.recordsProcessed ?? existingCard?.recordsProcessed ?? 0,
    totalRecords: event.totalRecords ?? existingCard?.totalRecords,
    currentStage: event.currentStage ?? existingCard?.currentStage,
    estimatedTimeRemaining: existingCard?.estimatedTimeRemaining,
    startedAt: event.startedAt ?? startedAtFallback,
    createdAt: event.startedAt ?? existingCard?.createdAt ?? startedAtFallback,
    currentRetry: existingCard?.currentRetry ?? 0,
    maxRetries: existingCard?.maxRetries ?? 3,
  }
  return {
    ...currentStatus,
    activeSyncs: existingCard
      ? currentStatus.activeSyncs.map((sync) => (sync.jobId === event.jobId ? mergedCard : sync))
      : [...currentStatus.activeSyncs, mergedCard],
  }
}

/**
 * Merge a `sync_progress` event into the snapshot.
 *
 * Monotonic per-card progress guard: the two emitters above run on
 * independent schedules, so a stale lower percentage can arrive AFTER a
 * fresher higher one (45 → 40) and visibly walk the bar backwards. Only a
 * progress >= the card's current value is applied; stage and records from
 * the same event still update. Terminal transitions are unaffected —
 * sync_completed / sync_failed never flow through this reducer (they
 * trigger a full fetchSyncStatus in the store instead).
 */
export function reduceSyncProgressEvent(
  currentStatus: SyncStatus,
  event: PartialSseSyncEvent,
): SyncStatus {
  return {
    ...currentStatus,
    activeSyncs: currentStatus.activeSyncs.map((sync) => {
      if (sync.jobId !== event.jobId) return sync
      // A card corrupted to a runtime-undefined progress (malformed payload)
      // counts as -1 so any real percentage can unfreeze it.
      const existingProgress = typeof sync.progress === 'number' ? sync.progress : -1
      const incomingProgress = event.progress
      const nextProgress =
        incomingProgress !== undefined && incomingProgress >= existingProgress
          ? incomingProgress
          : sync.progress
      return {
        // Partial events must never blank seeded fields — keep the card's
        // previous value whenever the incoming event omits a field.
        ...sync,
        progress: nextProgress,
        recordsProcessed: event.recordsProcessed ?? sync.recordsProcessed,
        currentStage: event.currentStage ?? sync.currentStage,
      }
    }),
  }
}
